<template>
  <div class="reservation-attendance-date-picker">
    <div
      v-gtm:click="GtmEvent.TIME_SELECTION_STARTED"
      class="selector-wrapper flex gap-2"
      :class="[isSoldOut ? 'cursor-not-allowed' : 'cursor-pointer']"
      @click="handleTimeSelectorClick"
    >
      <etg-input
        :value="bookingAttendanceString"
        class="pointer-events-none"
        :class="isSoldOut && 'bg-gray-30'"
        :aria-label="$t('reservation.date_picker.people')"
        placeholder=""
      >
        <template #prefix>
          <svg-icon name="person" class="size-4 mr-2" :class="isSoldOut ? 'text-gray-60' : 'text-black-50'" />
        </template>
      </etg-input>
      <etg-input
        :value="bookingDateString"
        class="pointer-events-none"
        :class="isSoldOut && 'bg-gray-30'"
        :aria-label="$t('reservation.date_picker.date')"
        placeholder=""
      >
        <template #prefix>
          <svg-icon name="calendar" class="size-4 mr-2" :class="isSoldOut ? 'text-gray-60' : 'text-black-50'" />
        </template>
      </etg-input>
    </div>

    <content-viewer
      v-if="!isSoldOut"
      v-model:show="isAttendanceDatePickerShown"
      :title="$t('reservation.date_picker.title')"
      main-content-class="pt-0"
      :is-full-height="true"
      :with-trigger-btn="false"
      :is-confirm-btn-disabled="isConfirmBtnDisabled"
      @confirm="handlePickerSubmit"
      @close="onViewerClose"
    >
      <template #default>
        <div class="people-root">
          <p class="py-4 text-lg font-500 md:font-400">{{ $t('reservation.date_picker.people') }}</p>
          <attendance-picker v-model="selectedAttendance" class="-mx-5 px-5 py-3" :options="attendanceOpts" />
          <div v-if="isPromoHintShown" class="text-red-40 bg-red-00 -mx-5 py-2 px-5 text-xs">{{ $t('reservation.date_picker.hint') }}</div>
        </div>
        <hr class="border-t-8 border-gray-20 -mx-5" />
        <div v-if="groupedByDateSlots" class="arrival-times-root relative -mx-5">
          <p class="py-4 px-5 text-lg font-500 md:font-400">{{ $t('reservation.date_picker.date') }}</p>
          <div ref="pickerRef" class="picker px-5">
            <date-picker-calendar
              v-show="arrivalTimes.slots"
              v-model="selectedDate"
              :arrival-times="arrivalTimes"
              :grouped-by-date-slots="groupedByDateSlots"
              :is-required="!!selectedDate"
            />
            <p v-show="!arrivalTimes.slots" class="py-4 text-gray-80 text-center">{{ $t('reservation.date_picker.no_time_slot') }}</p>
          </div>
        </div>
      </template>
    </content-viewer>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'

import { useAppStore } from '@/store/app'
import { useBranchStore } from '@/store/branch'

import ContentViewer from '@/components/common/content-viewer.vue'
import AttendancePicker from '@/components/common/attendance-picker.vue'
import EtgInput from '@/components/form/etg-input.vue'
import DatePickerCalendar from '@/components/reservation/date-picker-calendar.vue'

import type { BranchArrivalTimeInfo } from '@/types/api'
import { GtmEvent } from '@/types/gtm'
import { PROMO_CODE_LIMIT_MIN_PAX } from '@/constants/commons'

const nuxtApp = useNuxtApp()

const { t } = useI18n()

const props = defineProps<{
  arrivalTimes: BranchArrivalTimeInfo
  hasPromoTags: boolean
}>()

const appStore = useAppStore()
const branchStore = useBranchStore()
const { headcountInfo } = storeToRefs(branchStore)

const { branch, attendance, date, slotInfo, isSoldOut } = useBranchDetailContext()
const { formatDateWithI18n } = useI18nDateString()

const isAttendanceDatePickerShown = ref(false)
const selectedAttendance = ref(attendance.value)
const selectedDate = ref<Nullable<string>>(date.value)
const selectedSlot = ref<typeof slotInfo.value>(slotInfo.value)

const pickerRef = ref<Nullable<HTMLElement>>(null)

const { data: arrivalTimesRespData, isBranchArrivalTimesLoading } = await useGetBranchArrivalTimes('reservation-attendance-date-picker', {
  branch,
  attendance: selectedAttendance,
  asyncDataOpts: {
    immediate: false,
    watch: [selectedAttendance],
  },
})

const isPromoHintShown = computed(() => {
  const options = headcountInfo.value?.headcount || []
  const canUsePromo = options.some(e => e >= PROMO_CODE_LIMIT_MIN_PAX)
  return props.hasPromoTags && canUsePromo && selectedAttendance.value < PROMO_CODE_LIMIT_MIN_PAX
})

const arrivalTimes = computed(() => arrivalTimesRespData.value || props.arrivalTimes)
const groupedByDateSlots = computed(() => getGroupedByDateSlots(arrivalTimes.value?.slots))
const bookingAttendanceString = computed(() => {
  return isSoldOut.value ? '' : t('branch_detail.selection.people', { pax: attendance.value }, attendance.value)
})
const bookingDateString = computed(() => (isSoldOut.value || !date.value ? '' : formatDateWithI18n(date.value, 'MMMD')))
const isConfirmBtnDisabled = computed(() => !selectedDate.value)
const attendanceOpts = computed(() => headcountInfo.value?.headcount)

useReservationState({
  isViewerShown: isAttendanceDatePickerShown,
  selectedTimeSlotInfo: selectedSlot,
  selectedDate,
  groupedByDateSlots,
})

watch(isBranchArrivalTimesLoading, newIsBranchArrivalTimesLoading => {
  if (!isAttendanceDatePickerShown.value) {
    return
  }

  appStore.isFullPageLoadingShown = newIsBranchArrivalTimesLoading
})

watch(slotInfo, newSlotInfo => {
  if (selectedSlot.value?.slot !== newSlotInfo?.slot) {
    selectedSlot.value = newSlotInfo
  }
})

const handleTimeSelectorClick = () => {
  if (isSoldOut.value) {
    return
  }

  isAttendanceDatePickerShown.value = true
}

const handlePickerSubmit = () => {
  const isChanged = date.value !== selectedDate.value || attendance.value !== selectedAttendance.value
  const payload = {
    'Booking Date': date.value,
    Custnum: attendance.value,
    'Is Changed': isChanged,
    'New Booking Date': selectedDate.value,
    'New Custnum': selectedAttendance.value,
    branch: branch.value,
  }
  nuxtApp.$trackEvent({
    event: GtmEvent.TIME_SELECTION_CONFIRMED,
    payload,
  })

  attendance.value = selectedAttendance.value
  date.value = selectedDate.value
  slotInfo.value = selectedSlot.value
  branchStore.setArrivalTimes(arrivalTimes.value)
}

const onViewerClose = () => {
  selectedAttendance.value = attendance.value
  selectedDate.value = date.value
  selectedSlot.value = slotInfo.value
}
</script>
